import React from "react";
import { Box } from "@mui/material";
import { AccessTimeOutlined } from "@mui/icons-material";
import { dt } from "../../utils/dt";
import { ServiceReport, ServiceReportStatusCode, ServiceReportSourceType } from "../../models/ServiceReportsModel";
import { StatusBadgeColorConfig } from "../../../../shared/themes/redesignTokens";
import RedesignStatusBadge from "../../../../shared/components/redesign/RedesignStatusBadge";
import SourceTypeBadge from "../SourceTypeBadge";
import useTranslation from "../../../../shared/hooks/useTranslation";

// ===================================================================
//  STATUS BADGE CONFIG
// ===================================================================
const statusBadgeColors: Record<number, StatusBadgeColorConfig> = {
  [ServiceReportStatusCode.inProgress]: dt.serviceReports.status.inProgress,
  [ServiceReportStatusCode.solved]: dt.serviceReports.status.solved,
};

const statusLabelMap: Record<number, string> = {
  [ServiceReportStatusCode.inProgress]: "serviceReportStatus.inProgress",
  [ServiceReportStatusCode.solved]: "serviceReportStatus.solved",
};

const urgencyConfig: Record<number, { color: string; bg: string; labelKey: string }> = {
  1: { color: "#C0392B", bg: "rgba(192,57,43,0.08)", labelKey: "priorities.urgent" },
  2: { color: "#E67E22", bg: "rgba(230,126,34,0.08)", labelKey: "priorities.high" },
  4: { color: "#27AE60", bg: "rgba(39,174,96,0.08)", labelKey: "priorities.low" },
};

// ===================================================================
//  COMPONENT
// ===================================================================
interface SrHeaderStripProps {
  serviceReport: ServiceReport;
  resolutionText: string | null;
  isSolved: boolean;
}

const SrHeaderStrip = ({
  serviceReport,
  resolutionText,
  isSolved,
}: SrHeaderStripProps) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {/* Report number line */}
      <Box
        sx={{
          fontFamily: dt.fontMono,
          fontSize: "13px",
          fontWeight: 700,
          color: dt.blue,
          letterSpacing: "0.3px",
        }}
      >
        {translate("reportNo")} #{serviceReport.number}
      </Box>

      {/* Status badges row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <RedesignStatusBadge
          label={translate(statusLabelMap[serviceReport.statusCode])}
          colorConfig={statusBadgeColors[serviceReport.statusCode]}
        />

        {serviceReport.sourceType &&
          serviceReport.sourceType !== ServiceReportSourceType.manual && (
            <SourceTypeBadge
              sourceType={serviceReport.sourceType}
              serviceRequestInternalNumber={
                serviceReport.serviceRequestInternalNumber
              }
            />
          )}

        {resolutionText && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              fontSize: "12px",
              fontWeight: 600,
              fontFamily: dt.fontMono,
              color: isSolved
                ? dt.serviceReports.detail.solutionAccent
                : dt.blue,
              backgroundColor: isSolved
                ? "rgba(39,174,96,0.06)"
                : "rgba(52,152,219,0.06)",
              padding: "4px 10px",
              borderRadius: "12px",
            }}
          >
            <AccessTimeOutlined sx={{ fontSize: 14 }} />
            {resolutionText}
          </Box>
        )}

        {serviceReport.urgency != null && urgencyConfig[serviceReport.urgency] && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              fontSize: "12px",
              fontWeight: 600,
              fontFamily: dt.fontMono,
              color: urgencyConfig[serviceReport.urgency].color,
              backgroundColor: urgencyConfig[serviceReport.urgency].bg,
              padding: "4px 10px",
              borderRadius: "12px",
            }}
          >
            {translate(urgencyConfig[serviceReport.urgency].labelKey)}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SrHeaderStrip;
